/**
 * Official Flaha brand presentation for the application shell.
 *
 * Logo assets are the canonical marks from flaha.org/public/logos:
 *  - flaha-main.png     → Flaha Agri Tech (master brand)
 *  - flaha-pa.png       → Flaha PA (division)
 *  - apps/flahasoil.svg → FlahaSOIL (product)
 *
 * Shell (`full` / `mark`) keeps a single product mark so dark square
 * badges do not stack into a grey glass overlay in the top bar.
 * Marketing surfaces use `hero` / `family` for dual or triple lockups.
 */
import { Box, Stack, Typography } from "@mui/material";

import flahaAgriTechLogo from "../../assets/brand/flaha-agri-tech-logo.png";
import flahaPaLogo from "../../assets/brand/flaha-pa-logo.png";
import flahaSoilLogoSvg from "../../assets/brand/flahasoil-logo.svg";
import flahaSoilLogoPng from "../../assets/brand/flahasoil-logo.png";

import {
	BRAND_DIVISION,
	BRAND_ORG,
	BRAND_WORDMARK,
} from "../../theme/brand";

interface FlahaLogoProps {
	/** Primary product mark edge size in px. */
	size?: number;
	/**
	 * - `mark`   — FlahaSOIL product mark only (compact chrome)
	 * - `full`   — product mark + wordmark + parent line (app shell)
	 * - `family` — Agri Tech → PA → FlahaSOIL product-family chain
	 * - `hero`   — large dual lockup for landing / marketing surfaces
	 */
	variant?: "full" | "mark" | "family" | "hero";
	/** Text colour for wordmark / connectors (logos keep original colours). */
	color?: string;
}

interface BrandImageProps {
	src: string;
	label: string;
	size: number;
	/** Soft rounded clip so black square marks read cleanly on glass chrome. */
	rounded?: boolean;
}

function BrandImage({ src, label, size, rounded = true }: BrandImageProps) {
	const radius = rounded ? Math.max(6, Math.round(size * 0.18)) : 0;
	return (
		<Box
			component="img"
			src={src}
			alt={label}
			width={size}
			height={size}
			sx={{
				display: "block",
				width: size,
				height: size,
				objectFit: "contain",
				flexShrink: 0,
				borderRadius: `${radius}px`,
				// Avoid a second “grey plate” around the mark on dark chrome
				backgroundColor: "transparent",
				boxShadow: "none",
			}}
		/>
	);
}

function BrandDivider({ height, color }: { height: number; color: string }) {
	return (
		<Box
			aria-hidden="true"
			sx={{
				width: 1,
				height: Math.max(18, height * 0.55),
				flexShrink: 0,
				backgroundColor: color,
				opacity: 0.22,
			}}
		/>
	);
}

export function FlahaLogo({
	size = 36,
	variant = "full",
	color = "#F6F0E3",
}: FlahaLogoProps) {
	const productLogo = flahaSoilLogoSvg || flahaSoilLogoPng;

	if (variant === "mark") {
		return (
			<BrandImage src={productLogo} label={BRAND_WORDMARK} size={size} />
		);
	}

	if (variant === "family") {
		const familySize = Math.max(28, Math.round(size * 0.78));
		return (
			<Stack
				direction="row"
				alignItems="center"
				spacing={{ xs: 0.75, sm: 1 }}
				aria-label={`${BRAND_ORG} to ${BRAND_DIVISION} to ${BRAND_WORDMARK}`}
			>
				<BrandImage
					src={flahaAgriTechLogo}
					label={BRAND_ORG}
					size={familySize}
				/>
				<Typography
					aria-hidden="true"
					sx={{ color, opacity: 0.45, fontSize: Math.max(14, size * 0.32) }}
				>
					→
				</Typography>
				<BrandImage src={flahaPaLogo} label={BRAND_DIVISION} size={familySize} />
				<Typography
					aria-hidden="true"
					sx={{ color, opacity: 0.45, fontSize: Math.max(14, size * 0.32) }}
				>
					→
				</Typography>
				<BrandImage src={productLogo} label={BRAND_WORDMARK} size={size} />
			</Stack>
		);
	}

	if (variant === "hero") {
		const masterSize = Math.round(size * 0.92);
		return (
			<Stack
				direction="row"
				alignItems="center"
				spacing={{ xs: 1.5, sm: 2 }}
				aria-label={`${BRAND_WORDMARK} by ${BRAND_ORG}`}
			>
				<BrandImage src={productLogo} label={BRAND_WORDMARK} size={size} />
				<BrandDivider height={size} color={color} />
				<BrandImage
					src={flahaAgriTechLogo}
					label={BRAND_ORG}
					size={masterSize}
				/>
			</Stack>
		);
	}

	// `full` — shell lockup: ONE product mark + wordmark + parent attribution.
	// Second square logo intentionally omitted in chrome (avoids dual black
	// badges / grey overlay on the glass top bar).
	const wordSize = Math.max(15, Math.round(size * 0.48));
	const subSize = Math.max(10, Math.round(size * 0.28));

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.25,
				minWidth: 0,
				maxWidth: "100%",
			}}
			aria-label={`${BRAND_WORDMARK}, ${BRAND_ORG}`}
		>
			<BrandImage src={productLogo} label={BRAND_WORDMARK} size={size} />
			<Stack spacing={0.2} sx={{ minWidth: 0, justifyContent: "center" }}>
				<Typography
					component="span"
					sx={{
						fontWeight: 800,
						letterSpacing: "-0.02em",
						fontSize: wordSize,
						color,
						lineHeight: 1.1,
						whiteSpace: "nowrap",
					}}
				>
					{BRAND_WORDMARK}
				</Typography>
				<Typography
					component="span"
					sx={{
						fontSize: subSize,
						fontWeight: 600,
						letterSpacing: "0.04em",
						color,
						opacity: 0.72,
						lineHeight: 1.15,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{BRAND_ORG}
				</Typography>
			</Stack>
		</Box>
	);
}
